import { snakeCase } from 'lodash';
import { useMemo } from 'react';
import { useAccountMetaQuery } from 'queries';
import { useAccountDataQuery } from 'queries/account';
import { AccountFeature, useAccountFeature } from './use-account-feature';
import { useLocalStorage } from './use-local-storage';

export enum PrereleaseFeature {
  ShiftStartDateInResourceManagement = 'ShiftStartDateInResourceManagement',
  PaginateAllTask = 'PaginateAllTask',
  InferredProgress = 'InferredProgress',
  Gpt = 'Gpt',
  ProjectListPagination = 'ProjectListPagination',
  ProjectTasksListPagination = 'ProjectTasksListPagination',
  ShowSubscriptionPeriodHoursCarryOver = 'ShowSubscriptionPeriodHoursCarryOver',
  MeetingsAI = 'MeetingsAI',
  AccountsPortfolio = 'AccountsPortfolio',
  CustomerPortalTemplateField = 'CustomerPortalTemplateField',
  Spotlight = 'Spotlight',
  HideFormulaFields = 'HideFormulaFields',
  CpOverride = 'CpOverride',
  SpotlightOverride = 'SpotlightOverride',
  Marketplace = 'Marketplace',
  TSReportingLine = 'TSReportingLine',
  MultiLineBudgets = 'MultiLineBudgets',
  MultipleMeetingsInAiFill = 'MultipleMeetingsInAiFill',
  // LanguageConfigurations = 'LanguageConfigurations',
  AiFillContentStreaming = 'AiFillContentStreaming',
  ExpenseManagement = 'ExpenseManagement',
  Sheets = 'Sheets',
  AntdRlUISwitcher = 'AntdRlUISwitcher',
  TaskPaneUIRevamp = 'TaskPaneUIRevamp',
  CurrentPhasesOptimized = 'CurrentPhasesOptimized',
  ProjectListColumnPagination = 'ProjectListColumnPagination',
  ScheduledExports = 'ScheduledExports',
  EnableCronAutomation = 'EnableCronAutomation',
  SlackPvtChannelsFeature = 'SlackPvtChannelsFeature',
}

export const allowed_hostnames = [
  'localhost',
  'northstar.nitro.run',
  'bangalore.nitro.run',
  'testing.nitro.run',
  'rmroles.orbits.run',
  'deepakkumar.nitro.run',
  'report1.orbits.run',
  'report2.orbits.run',
];
const default_meetings_ai_enabled_hostnames = ['success.rocketlane.com'];

export const default_spotlight_override_enabled_hostnames = [
  'phorest.rocketlane.com',
  'onboarding.phorest.com',
];

export const excluded_features_localhost = window.Cypress
  ? [PrereleaseFeature.PaginateAllTask]
  : [];

export const useFeature = (
  feature: PrereleaseFeature,
  offByDefault = false,
): boolean => {
  const [value] = useLocalStorage(
    `__features.v2.${snakeCase(feature)}`,
    !offByDefault && allowed_hostnames.includes(window.location.hostname),
  );

  // added for cypress - need to remove once the feature is live
  if (window.location.hostname.includes('localhost') && window.Cypress) {
    return !excluded_features_localhost.includes(feature);
  }
  // enable meeting ai by default for success.rocketlane.com
  if (
    [PrereleaseFeature.MeetingsAI].includes(feature) &&
    default_meetings_ai_enabled_hostnames.includes(window.location.hostname)
  ) {
    return true;
  }

  // TODO: Remove this once the feature is live
  if (
    feature === PrereleaseFeature.SpotlightOverride ||
    feature === PrereleaseFeature.Marketplace ||
    feature === PrereleaseFeature.CpOverride
  ) {
    return true;
  }

  return value;
};

export const useIsCustomerPortalEnabled = () => {
  return useMemo(() => true, []);
};

export const useIsTaskPaneOptimizationEnabled = () => {
  return true;
};

// const BETA_ACCOUNTS = [107, 1];

export const useNewDashboardEnabled = () => {
  const { data, isLoading, isError } = useAccountMetaQuery();

  if (isLoading || isError) {
    return false;
  }

  // if (BETA_ACCOUNTS.includes(account.accountId)) {
  //   return true;
  // }

  return data?.features.dashboardV2 ?? false;
};

const CURRENT_PHASES_OPTIMIZED_ACCOUNTS = [
  22192, // HighRadius
];

export const useIsCurrentPhasesOptimizedEnabled = () => {
  const isEnabled = useFeature(PrereleaseFeature.CurrentPhasesOptimized);
  const { data: initData } = useAccountDataQuery();
  const accountId = initData?.account?.accountId;

  if (accountId && CURRENT_PHASES_OPTIMIZED_ACCOUNTS.includes(accountId)) {
    return true;
  }

  return isEnabled;
};

export const useIsEnableCronAutomationEnabled = () => {
  const isEnabled = useFeature(PrereleaseFeature.EnableCronAutomation);

  if (window.location.hostname.includes('localhost')) {
    return false;
  }

  return isEnabled;
};

export const useIsCreditNotesFeatureEnabled = () => {
  const { data } = useAccountDataQuery();

  const [isCreditNotesFeatureEnabled] = useAccountFeature(
    AccountFeature.CreditNotes,
  );

  return (
    isCreditNotesFeatureEnabled ??
    data?.accountMeta?.features?.creditNotes ??
    false
  );
};

export const useScheduledExportsEnabled = () => {
  const { data, isLoading, isError } = useAccountMetaQuery();

  if (isLoading || isError) {
    return false;
  }

  return data?.features.scheduledExports ?? false;
};

export const useGtmCodeBlockEnabled = () => {
  const { data, isLoading, isError } = useAccountMetaQuery();

  if (isLoading || isError) {
    return false;
  }

  return data?.features.gtmCodeBlock ?? false;
};

export const useSalesforceV2Enabled = () => {
  const { data, isLoading, isError } = useAccountMetaQuery();

  if (isLoading || isError) {
    return false;
  }

  return data?.features.salesforceIntegrationV2 ?? false;
};
